import { Injectable, computed, inject, signal } from '@angular/core';
import { MonthlyReport } from '../models/report.model';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ScanTablePaginator } from '../models/scanTablePaginator.model';
import { NewScan } from '../models/processedVin/newScan.model';
import { Scan } from '../models/scan.model';
import {UnProcessedSoldVin} from '../models/soldUnprocessed/unProcessedSoldVin.model';
import {UnProcessedVin} from '../models/unprocessedVin/UnProcessedVin.model';
import { ActivityByDateChart } from '../models/processedVin/activityByDateChart.model';
import { ActivityByDateChartResponse } from '../models/processedVin/activityByDateChartResponse.model';
import { environment } from '../../environments/environment';
import { SettingsService, utcOffsetValue } from './settings.service';
import { toCsv } from '../shared/csv';
import { SoldUnprocessedCounts } from '../models/landingPage/soldUnprocessedCounts.model';
import { UnprocessedVsProcessedCounts } from '../models/landingPage/unprocessedVsProcessedCounts.model';

const DEFAULT_PAGINATOR: ScanTablePaginator = {
  pageIndex: 0,
  cursorPrev: null,
  hasPrev: false,
  cursorNext: null,
  hasNext: false,
  total: 0,
  pageSize: 10
};

const DEFAULT_ACTIVITYBYDATECHARTDATA: ActivityByDateChart = {
    dates: [],
    activity: [
      {
        status: 'Completed',
        scans: [0]
      },
      {
        status: 'Exception Reported',
        scans: [0]
      },
      {
        status: 'Abandoned',
        scans: [0]
      },
      {
        status: 'Unsuccessful',
        scans: [0]
      }
    ]
  };

const DEFAULT_SOLDUNPROCESSEDCOUNTS: SoldUnprocessedCounts = {
  last24Hours: 0,
  last7Days: 0,
  last30Days: 0
};

const DEFAULT_UNPROCESSEDVSPROCESSEDCOUNTS: UnprocessedVsProcessedCounts = {
  unprocessedCount: 0,
  processedCount: 0
};

/** Tooltip on the server CSV export buttons, whose download pop-up blockers often catch. */
export const CSV_DOWNLOAD_HINT =
  'Download information: Your browser may display a pop-up or download permission prompt ' +
  'when exporting a CSV file. Please allow the download to proceed if prompted';

/**
 * Holds the application's report data and exposes it to components.
 *
 * This service is a pure state store: it does NOT make HTTP calls. The
 * ApiService fetches data and pushes it in via the setter methods below;
 * components read it through the readonly signals.
 */
@Injectable({
  providedIn: 'root'
})
export class DataService {

  private readonly http = inject(HttpClient);
  private readonly settings = inject(SettingsService);
  private readonly _monthlyReports = signal<MonthlyReport[]>([]);

  private readonly _dateRange = signal<{ start: Date; end: Date } | null>(null, {
    equal: (a, b) =>
      a?.start.getTime() === b?.start.getTime() && a?.end.getTime() === b?.end.getTime()
  });

  private readonly _scans = signal<Scan[]>([]);
  private readonly _newScans = signal<NewScan[]>([]);
  private readonly _newUnprocessedVinScans = signal<UnProcessedVin[]>([]);
  private readonly _newSoldUnprocessedVinScans = signal<UnProcessedSoldVin[]>([]);

  private _scanUnprocessedVinTablePagination = signal<ScanTablePaginator>(DEFAULT_PAGINATOR);
  private _scanSoldUnprocessedVinTablePagination = signal<ScanTablePaginator>(DEFAULT_PAGINATOR);

  private _scanTablePagination = signal<ScanTablePaginator>(DEFAULT_PAGINATOR);

  private readonly _activityByDateChartData = signal<ActivityByDateChart>(DEFAULT_ACTIVITYBYDATECHARTDATA);

  private readonly _soldUnprocessedCounts = signal<SoldUnprocessedCounts>(DEFAULT_SOLDUNPROCESSEDCOUNTS);

  private readonly _unprocessedVsProcessedCounts = signal<UnprocessedVsProcessedCounts>(DEFAULT_UNPROCESSEDVSPROCESSEDCOUNTS);
  
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  /** Report rows currently held in the store. */
  readonly monthlyReports = this._monthlyReports.asReadonly();
  /** Raw scan records loaded for the current processor + date range. */
  readonly scans = this._scans.asReadonly();

  readonly newScans = this._newScans.asReadonly();
  readonly newScansVin = this._newUnprocessedVinScans.asReadonly();
  readonly newScansSoldVin = this._newSoldUnprocessedVinScans.asReadonly();

  readonly scanTablePagination = this._scanTablePagination.asReadonly();
  readonly scanUnprocessedVinTablePagination = this._scanUnprocessedVinTablePagination.asReadonly();
  readonly scanSoldUnprocessedVinTablePagination = this._scanSoldUnprocessedVinTablePagination.asReadonly();

  readonly activityByDateChartData = this._activityByDateChartData.asReadonly();

  readonly soldUnprocessedCounts = this._soldUnprocessedCounts.asReadonly();

  readonly unprocessedVsProcessedCounts = this._unprocessedVsProcessedCounts.asReadonly();

  // Data filters live in DataFilterService. Filtering is server-side, so the
  // rows held here are already the filtered rows — there is no separate
  // filtered view to derive.

  /** Date range the current scans were loaded for. */
  readonly dateRange = this._dateRange.asReadonly();
  /** True while an API request is in flight. */
  readonly loading = this._loading.asReadonly();
  /** Last error message, or null if the last load succeeded. */
  readonly error = this._error.asReadonly();

  /** Derived totals, recomputed automatically when the data changes. */
  readonly totalGenerated = computed(() =>
    this._monthlyReports().reduce((sum, r) => sum + r.generated, 0)
  );
  readonly totalExported = computed(() =>
    this._monthlyReports().reduce((sum, r) => sum + r.exported, 0)
  );

  /** Total number of scans currently held. */
  readonly totalScans = computed(() => this._scans().length);

  // Status aggregations now live in shared/scan-stats.ts so they can be reused
  // for any status field (Auto-Cleared, Disconnected Car, …).

  // --- Mutations (called by ApiService) ---

  setMonthlyReports(reports: MonthlyReport[]): void {
    this._monthlyReports.set(reports);
  }

  setScans(scans: Scan[]): void {
    this._scans.set(scans);
  }

  setDefaultScanTablePagination() {
    this._scanTablePagination.set(DEFAULT_PAGINATOR);
  }

  setDefaultActivityByDateChartData() {
    this._activityByDateChartData.set(DEFAULT_ACTIVITYBYDATECHARTDATA);
  }

  setDefaultNewScans() {
    this._newScans.set([]);
  }

  setNewScans(scans: NewScan[],hasNext: boolean, nextCursor: string|null, hasPrevious: boolean, previousCursor: string|null, total: number, pageSize: number): void {

    this._newScans.set(scans);

    let updatedPaginator: ScanTablePaginator = this._scanTablePagination();

    updatedPaginator.hasNext = hasNext;
    updatedPaginator.cursorNext = nextCursor;
    updatedPaginator.hasPrev = hasPrevious;
    updatedPaginator.cursorPrev = previousCursor;
    updatedPaginator.total = total;
    updatedPaginator.pageSize = pageSize;

    this._scanTablePagination.set(updatedPaginator);

  }

  setNewUnprocessedVin(scans: UnProcessedVin[], hasNext: boolean, nextCursor: string|null, hasPrevious: boolean, previousCursor: string|null, total: number, pageSize: number): void {

    this._newUnprocessedVinScans.set(scans);

    let updatedPaginator: ScanTablePaginator = this._scanTablePagination();

    updatedPaginator.hasNext = hasNext;
    updatedPaginator.cursorNext = nextCursor;
    updatedPaginator.hasPrev = hasPrevious;
    updatedPaginator.cursorPrev = previousCursor;
    updatedPaginator.total = total;
    updatedPaginator.pageSize = pageSize;

    this._scanTablePagination.set(updatedPaginator);

  }

  setNewSoldUnprocessedVin(scans: UnProcessedSoldVin[], hasNext: boolean, nextCursor: string|null, hasPrevious: boolean, previousCursor: string|null, total: number, pageSize: number): void {

    this._newSoldUnprocessedVinScans.set(scans);

    let updatedPaginator: ScanTablePaginator = this._scanSoldUnprocessedVinTablePagination();

    updatedPaginator.hasNext = hasNext;
    updatedPaginator.cursorNext = nextCursor;
    updatedPaginator.hasPrev = hasPrevious;
    updatedPaginator.cursorPrev = previousCursor;
    updatedPaginator.total = total;
    updatedPaginator.pageSize = pageSize;

    this._scanSoldUnprocessedVinTablePagination.set(updatedPaginator);

  }

  prepareChartData(rawDataResponse: ActivityByDateChartResponse) {

    let responseData = rawDataResponse.data.data;

    let chartData: ActivityByDateChart = {
      dates: responseData.map(data => {
        return data.date;
      }),
      activity: []
    }

    chartData.activity = [
      {
        status: 'Exception Reported',
        scans: chartData.dates.map(date => {return 0})
      },
      {
        status: 'Unsuccessful',
        scans: chartData.dates.map(date => {return 0})
      },
      {
        status: 'Abandoned',
        scans: chartData.dates.map(date => {return 0})
      },
      {
        status: 'Completed',
        scans: chartData.dates.map(date => {return 0})
      }
    ]

    /* let dates: string[] = responseData.map(data => {
      return data.date;
    });

    let activity: {status: string, scans: number[]}[] = [
      {
        status: 'Completed',
        scans: [0]
      },
      {
        status: 'Exception Reported',
        scans: [0]
      },
      {
        status: 'Abandoned',
        scans: [0]
      },
      {
        status: 'Unsuccessful',
        scans: [0]
      }
    ]; */

    responseData.forEach(activityByDate => {

      chartData.activity[0].scans[chartData.dates.indexOf(activityByDate.date)] += activityByDate.exception_reported;

      chartData.activity[1].scans[chartData.dates.indexOf(activityByDate.date)] += activityByDate.unsuccessful;

      chartData.activity[2].scans[chartData.dates.indexOf(activityByDate.date)] += activityByDate.abandoned;

      chartData.activity[3].scans[chartData.dates.indexOf(activityByDate.date)] += activityByDate.completed;

    })

    if (!environment.production) {
      console.log('Proc VIN Chart Data:');
      console.log(chartData);
    }

    this.setActivityByDateChartData(chartData);

  }

  setActivityByDateChartData(chartData: ActivityByDateChart) {
    this._activityByDateChartData.set(chartData);
  }

  setSoldUnprocessedCounts(soldUnprocessedCounts: SoldUnprocessedCounts) {
    this._soldUnprocessedCounts.set(soldUnprocessedCounts);
  }

  setUnprocessedVsProcessedCounts(unprocessedVsProcessedCounts: UnprocessedVsProcessedCounts) {
    this._unprocessedVsProcessedCounts.set(unprocessedVsProcessedCounts);
  }

  /** Null clears the range — see FilterBarComponent.ngOnDestroy. */
  setDateRange(range: { start: Date; end: Date } | null): void {
    this._dateRange.set(range);
  }

  setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  setError(error: string | null): void {
    this._error.set(error);
  }

  clear(): void {
    this._monthlyReports.set([]);
    this._scans.set([]);
    this._dateRange.set(null);
    this._error.set(null);
  }

  downloadFile(url: string): Observable<Blob> {
    return this.http.get(url, { responseType: 'blob' });
  }

  // --- Activity by Date chart export ---

  /**
   * The Activity by Date chart as a downloadable CSV — filename and contents —
   * or null when there is nothing loaded to export.
   *
   * Built from the chart data the panel is already rendering, so the file and
   * the bars can never disagree and no second request is made. The status
   * columns are read off the chart series, so they inherit whatever
   * prepareChartData produced: same labels, same order as the legend.
   */
  activityByDateChartCsvFile(): { filename: string; content: string } | null {

    const chartData = this._activityByDateChartData();
    const dateRange = this._dateRange();

    if (!chartData.dates.length || dateRange == null) {
      return null;
    }

    const headers = ['Date', ...chartData.activity.map(series => series.status), 'Total'];

    const rows = chartData.dates.map((bucket, index) => {
      // ?? 0 because the empty-state chart holds four series of one point
      // against zero dates — the two lengths are not guaranteed to agree.
      const counts = chartData.activity.map(series => series.scans[index] ?? 0);
      return [
        this.formatBucketDate(bucket),
        ...counts,
        counts.reduce((sum, count) => sum + count, 0)
      ];
    });

    return {
      filename: this.activityByDateChartCsvFilename(dateRange),
      content: toCsv(headers, rows)
    };
  }

  /**
   * A chart bucket as a display date in the user's locale.
   *
   * A bucket is a calendar day serialized at UTC midnight
   * ("2026-08-01T00:00:00Z"), so it is rebuilt from its parts and formatted in
   * UTC. Letting Date parse it and formatting in the browser's zone lands on
   * the previous day anywhere west of Greenwich.
   *
   * 'medium' renders the month as a name — "Aug 1, 2026" / "1 Aug 2026" —
   * which follows the locale without the ambiguity of a numeric d/m/y that a
   * spreadsheet set to a different locale would silently reinterpret.
   */
  private formatBucketDate(bucket: string): string {
    const [year, month, day] = bucket.slice(0, 10).split('-').map(Number);
    return new Intl.DateTimeFormat(this.settings.locale(), {
      dateStyle: 'medium',
      timeZone: 'UTC'
    }).format(new Date(Date.UTC(year, month - 1, day)));
  }

  /**
   * Range in ISO form so exports sort chronologically in a folder, plus the
   * offset the counts were bucketed in. Taken from the picker rather than the
   * response's start_date, which is the offset-shifted instant and reads a day
   * early wherever the offset is positive.
   */
  private activityByDateChartCsvFilename(dateRange: { start: Date; end: Date }): string {
    const iso = (date: Date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const offset = utcOffsetValue(this.settings.timezone(), dateRange.start).replace(':', '');
    return `activity-by-date_${iso(dateRange.start)}_${iso(dateRange.end)}_UTC${offset}.csv`;
  }
}
